import { useState, forwardRef, useImperativeHandle, useRef, useId } from 'react';

import type { ChangeEvent, JSX } from 'react';
import type { LinkType } from 'components/RteEditor/Plugins/LinkPlugin';

import styles from './AddLinkForm.module.css';

import { ensureHttps, validateUrlWithProtocol } from 'components/RteEditor/Plugins/LinkPlugin/helpers';
import { Button, FeedbackBox, FormField, Icon, InputField, Label } from 'components';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

interface AddLinkFormProps {
  onCloseHandler: () => void;
  onSaveHandler: (url: string, linktext: string) => void;
  isLink: LinkType;
}

export interface addLinkFormMethods {
  clearForm: () => void;
  focusFirstField: () => void;
  setDefaults: (url: string, linktext: string) => void;
}

export const AddLinkForm = forwardRef(
  (
    { onCloseHandler, onSaveHandler, isLink = { active: false, url: '', linktext: '' } }: AddLinkFormProps,
    ref
  ): JSX.Element => {
    const t = useTranslation();
    const [url, setUrl] = useState<string>(isLink.url || '');
    const [linkText, setLinkText] = useState<string>(isLink.linktext || '');
    const [showLinktextField, setShowLinktextField] = useState<boolean>(false);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [linkTextError, setLinkTextError] = useState<string | null>(null);
    const firstField = useRef<HTMLInputElement>(null);
    const linkId = `link_${useId()}`;
    const textId = `text_${useId()}`;

    const handleOpenUrl = () => {
      window.open(ensureHttps(url), '_blank');
    };

    const handleUrl = (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.target.value);
    };

    const handleLinkText = (event: ChangeEvent<HTMLInputElement>) => {
      setLinkText(event.target.value);
    };

    const validateUrl = (url: string): boolean => {
      if (validateUrlWithProtocol(url) === false || url.trim() === '') {
        setUrlError(t.errortext.urlNotValid);
        return false;
      }
      setUrlError(null);
      return true;
    };

    const validateLinkText = (linkText: string): boolean => {
      if (linkText.trim() === '') {
        setLinkTextError(t.errortext.linkTextNotValid);
        return false;
      }
      setLinkTextError(null);
      return true;
    };

    const handleSave = () => {
      if (!validateUrl(url) || (showLinktextField && !validateLinkText(linkText))) {
        return;
      }

      onSaveHandler(url, linkText);
      resetForm();
    };

    const handleClose = () => {
      onCloseHandler();
      resetForm();
    };

    const resetForm = () => {
      setUrl('');
      setShowLinktextField(false);
      setUrlError(null);
      setLinkTextError(null);
    };

    const setDefaults = (url: string, linktext: string) => {
      setUrl(url);
      if (!linktext && !url) {
        setShowLinktextField(true);
      }
    };

    const focusFirstField = () => {
      if (firstField.current) {
        firstField.current.focus();
      }
    };

    useImperativeHandle(ref, () => ({
      clearForm: resetForm,
      focusFirstField,
      setDefaults,
    }));

    return (
      <div className={styles['linkform']}>
        <FormField className={styles['linkform__input-field']}>
          <Label htmlFor={linkId}>{t.links.linkLabel}</Label>
          <div className={styles['linkform__input-url-wrapper']}>
            <InputField
              id={linkId}
              placeholder={t.links.linkPlaceholder}
              value={url}
              onChange={(event) => handleUrl(event)}
              onFocus={() => setUrlError(null)}
              ref={firstField}
              aria-label={t.aria.labels.linkField}
            />
            {url && (
              <button type="button" onClick={handleOpenUrl} className={styles['linkform__open-url-button']}>
                <Icon name="ArrowTopRightOnSquare" size={20} />
              </button>
            )}
          </div>
          {urlError && <FeedbackBox level="error" feedback={urlError} />}
        </FormField>
        {showLinktextField && (
          <FormField className={styles['linkform__input-field']}>
            <Label htmlFor={textId}>{t.links.linkText}</Label>
            <InputField
              id={textId}
              value={linkText}
              onChange={(event) => handleLinkText(event)}
              onFocus={() => setLinkTextError(null)}
              aria-label={t.aria.labels.linkTextfield}
            />
            {linkTextError && <FeedbackBox level="error" feedback={linkTextError} />}
          </FormField>
        )}
        <div className={styles['linkform__footer']}>
          <Button variant={'secondary'} onClick={handleClose} aria-label={t.common.cancel} type="button">
            {t.common.cancel}
          </Button>
          <Button variant={'primary'} onClick={handleSave} aria-label={t.aria.labels.linkOkButton} type="button">
            {t.links.addLink}
          </Button>
        </div>
      </div>
    );
  }
);
